import { safeFormatDate } from '@/utils/format-date';
import type { ArtifactListItem, ArtifactSortKey } from '@/types/artifacts';

export interface ArtifactRunGroup {
  key: string;
  workflowId: string | null;
  workflowName: string;
  runId: string | null;
  runCreatedAt: string | null;
  runDateLabel: string;
  artifacts: ArtifactListItem[];
}

function getGroupKey(artifact: ArtifactListItem): string {
  const workflowId = artifact.source_workflow_id ?? 'unknown-workflow';
  const runId = artifact.source_run_id ?? 'unknown-run';
  return `${workflowId}:${runId}`;
}

function getRunCreatedAt(artifact: ArtifactListItem): string | null {
  return artifact.source_run_created_at ?? artifact.created_at ?? null;
}

function pickRepresentativeArtifact(
  groupArtifacts: ArtifactListItem[],
): ArtifactListItem {
  return (
    groupArtifacts.find((artifact) => artifact.source_run_created_at) ??
    groupArtifacts[0]
  );
}

function getWorkflowName(artifact: ArtifactListItem): string {
  if (artifact.source_workflow_name) return artifact.source_workflow_name;
  return artifact.source_workflow_id ? 'Unknown workflow' : 'Other artifacts';
}

function formatArtifactRunDate(isoString: string | null): string {
  if (!isoString) return '';
  return safeFormatDate(new Date(isoString), 'EEEE MMMM d, h:mmaaa');
}

function getTime(dateString: string): number {
  const time = new Date(dateString).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function compareGroups(
  a: ArtifactRunGroup,
  b: ArtifactRunGroup,
  sort: ArtifactSortKey,
): number {
  switch (sort) {
    case 'title_asc': {
      const nameCmp = a.workflowName.localeCompare(b.workflowName, undefined, {
        sensitivity: 'base',
      });
      if (nameCmp !== 0) return nameCmp;
      return compareRunDateDesc(a, b);
    }
    case 'title_desc': {
      const nameCmp = b.workflowName.localeCompare(a.workflowName, undefined, {
        sensitivity: 'base',
      });
      if (nameCmp !== 0) return nameCmp;
      return compareRunDateDesc(a, b);
    }
    case 'updated_at_asc':
      return compareRunDateAsc(a, b);
    case 'updated_at_desc':
    default:
      return compareRunDateDesc(a, b);
  }
}

function compareRunDateDesc(a: ArtifactRunGroup, b: ArtifactRunGroup): number {
  const aTime = a.runCreatedAt ? getTime(a.runCreatedAt) : 0;
  const bTime = b.runCreatedAt ? getTime(b.runCreatedAt) : 0;
  if (aTime !== bTime) return bTime - aTime;

  const aUpdated = a.artifacts.length
    ? Math.max(...a.artifacts.map((art) => getTime(art.updated_at)))
    : 0;
  const bUpdated = b.artifacts.length
    ? Math.max(...b.artifacts.map((art) => getTime(art.updated_at)))
    : 0;
  return bUpdated - aUpdated;
}

function compareRunDateAsc(a: ArtifactRunGroup, b: ArtifactRunGroup): number {
  return -compareRunDateDesc(a, b);
}

export function groupArtifactsByRun(
  artifacts: ArtifactListItem[],
  sort: ArtifactSortKey,
): ArtifactRunGroup[] {
  const grouped = new Map<string, ArtifactListItem[]>();

  for (const artifact of artifacts) {
    const key = getGroupKey(artifact);
    const existing = grouped.get(key);
    if (existing) {
      existing.push(artifact);
    } else {
      grouped.set(key, [artifact]);
    }
  }

  const groups: ArtifactRunGroup[] = [];

  for (const [key, groupArtifacts] of grouped) {
    const representative = pickRepresentativeArtifact(groupArtifacts);
    const runCreatedAt = getRunCreatedAt(representative);

    groups.push({
      key,
      workflowId: representative.source_workflow_id ?? null,
      workflowName: getWorkflowName(representative),
      runId: representative.source_run_id ?? null,
      runCreatedAt,
      runDateLabel: formatArtifactRunDate(runCreatedAt),
      artifacts: groupArtifacts,
    });
  }

  return groups.sort((a, b) => compareGroups(a, b, sort));
}
